import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, query } from "../api/client";
import { useQuery } from "../api/useQuery";
import type { GeoTerritory, User } from "../api/types";

type AssignmentStatus = "planned" | "active" | "done" | "missed" | "cancelled";

const STATUS: Record<AssignmentStatus, { name: string; badge: string }> = {
  planned: { name: "Запланирован", badge: "" },
  active: { name: "Выполняется", badge: " warn" },
  done: { name: "Выполнен", badge: " ok" },
  missed: { name: "Не выполнен", badge: " danger" },
  cancelled: { name: "Отменён", badge: "" },
};

interface Assignment {
  id: string;
  date: string;
  route_id: string;
  route_name: string;
  user_id: string;
  user_name: string;
  territory_name: string;
  note: string;
  status: AssignmentStatus;
  patrol_id: string | null;
  created_by: string;
  can_edit: boolean;
}

/** Дата YYYY-MM-DD по местному календарю, со сдвигом в днях. */
function isoDate(shiftDays = 0, from = new Date()): string {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + shiftDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function parseDay(iso: string): Date {
  const [y = 0, m = 1, d = 1] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

const dayTitle = new Intl.DateTimeFormat("ru-RU", { weekday: "short", day: "numeric", month: "long" });

function fmtDay(iso: string): string {
  const title = dayTitle.format(parseDay(iso));
  if (iso === isoDate()) return `${title} · сегодня`;
  if (iso === isoDate(1)) return `${title} · завтра`;
  return title;
}

/**
 * Назначение маршрутов: начальник назначает инспектору маршрут на дату,
 * приложение показывает назначенное. Статус выводится из обходов.
 */
export function AssignmentsPage() {
  const [from, setFrom] = useState(isoDate());
  const [inspector, setInspector] = useState("");
  const to = isoDate(6, parseDay(from));
  const users = useQuery<{ items: User[] }>("/api/v1/users");
  const geo = useQuery<{ items: GeoTerritory[] }>("/api/v1/geo");
  const items = useQuery<{ items: Assignment[] }>(
    "/api/v1/assignments" + query({ from, to, inspector_id: inspector }));

  const inspectors = useMemo(
    () => (users.data?.items ?? []).filter((u) => u.role === "inspector" && u.is_active),
    [users.data]);

  const byDate = useMemo(() => {
    const out = new Map<string, Assignment[]>();
    for (const a of items.data?.items ?? []) out.set(a.date, [...(out.get(a.date) ?? []), a]);
    return out;
  }, [items.data]);

  return (
    <>
      <div className="page-head">
        <h2>Назначения</h2>
      </div>

      <AssignForm inspectors={inspectors.filter((u) => u.can_edit)} territories={geo.data?.items ?? []}
        onDone={() => items.reload()} />

      <div className="filters">
        <button className="secondary" onClick={() => setFrom(isoDate(-7, parseDay(from)))}>← Неделя</button>
        <label className="field">
          С даты
          <input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </label>
        <button className="secondary" onClick={() => setFrom(isoDate(7, parseDay(from)))}>Неделя →</button>
        <label className="field">
          Инспектор
          <select value={inspector} onChange={(e) => setInspector(e.target.value)}>
            <option value="">Все</option>
            {inspectors.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </label>
        {(from !== isoDate() || inspector) && (
          <button className="secondary" onClick={() => { setFrom(isoDate()); setInspector(""); }}>Сбросить</button>
        )}
      </div>

      {items.error && <div className="error" role="alert">{items.error}</div>}
      {items.data && items.data.items.length === 0 && (
        <p className="notice">На эту неделю назначений нет.</p>
      )}

      {[...byDate].map(([date, list]) => (
        <div key={date} style={{ marginBottom: 16 }}>
          <div className="section-title">{fmtDay(date)}</div>
          <div className="list">
            {list.map((a) => <AssignmentRow key={a.id} a={a} onChange={() => items.reload()} />)}
          </div>
        </div>
      ))}
    </>
  );
}

function AssignmentRow({ a, onChange }: { a: Assignment; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelled = a.status === "cancelled";

  async function cancel() {
    if (!confirm(`Отменить назначение «${a.route_name}» для ${a.user_name}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/assignments/${a.id}/cancel`);
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось отменить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row" style={{ alignItems: "center", opacity: cancelled ? 0.6 : 1 }}>
      <span>
        <strong>{a.route_name}</strong> · {a.user_name}
        <span className="row-sub">
          {a.note && `${a.note} · `}назначил {a.created_by}
          {error && <span style={{ color: "var(--danger)" }}> · {error}</span>}
        </span>
      </span>
      <span style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
        {a.patrol_id
          ? <Link to={`/patrols/${a.patrol_id}`} className={`badge${STATUS[a.status].badge}`}>{STATUS[a.status].name}</Link>
          : <span className={`badge${STATUS[a.status].badge}`}>{STATUS[a.status].name}</span>}
        {a.can_edit && (a.status === "planned" || a.status === "missed") && (
          <button className="secondary" disabled={busy} onClick={() => void cancel()}>Отменить</button>
        )}
      </span>
    </div>
  );
}

function AssignForm({ inspectors, territories, onDone }: {
  inspectors: User[];
  territories: GeoTerritory[];
  onDone: () => void;
}) {
  const [form, setForm] = useState({ date: isoDate(), user_id: "", route_id: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Маршруты — только территории выбранного инспектора и только включённые.
  const userId = form.user_id || inspectors[0]?.id || "";
  const territory = inspectors.find((u) => u.id === userId)?.territory_id;
  const routes = territories.find((t) => t.id === territory)?.routes.filter((r) => r.is_active) ?? [];
  const routeId = routes.some((r) => r.id === form.route_id) ? form.route_id : routes[0]?.id ?? "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await api.post("/api/v1/assignments", { ...form, user_id: userId, route_id: routeId });
      setDone(`Назначено: ${routes.find((r) => r.id === routeId)?.name}, ${fmtDay(form.date)}`);
      setForm({ ...form, note: "" });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось назначить");
    } finally {
      setBusy(false);
    }
  }

  if (inspectors.length === 0) return null;

  return (
    <section className="card" style={{ marginBottom: 20 }}>
      <form onSubmit={(e) => void submit(e)}>
        <div className="filters" style={{ margin: 0 }}>
          <label className="field">Дата
            <input type="date" required min={isoDate()} value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </label>
          <label className="field">Инспектор
            <select value={userId} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
              {inspectors.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </label>
          <label className="field">Маршрут
            <select value={routeId} onChange={(e) => setForm({ ...form, route_id: e.target.value })}>
              {routes.length === 0 && <option value="">Нет маршрутов</option>}
              {routes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: "1 1 200px" }}>Задание
            <input value={form.note} placeholder="Необязательно" onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </label>
          <button type="submit" disabled={busy || !routeId}>Назначить</button>
        </div>
        {error && <div className="error" role="alert" style={{ marginTop: 12 }}>{error}</div>}
        {done && <p className="muted small" style={{ margin: "12px 0 0" }}>{done}. Инспектор увидит его в приложении после синхронизации.</p>}
      </form>
    </section>
  );
}
